import React, { useEffect, useRef, useState } from "react";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Confetti from "react-confetti";
import AudioService, { AUDIO_EFFECT } from "../../../../services/AudioService";
import { usePagesTranslation } from "../../../locales";

export default function WordWheelCompleteDialog({
    open,
    onClose,
    onContinue,
    isDaily,
}) {
    const { t } = usePagesTranslation();
    const hostRef = useRef(null);
    const [size, setSize] = useState({ w: 300, h: 280 });

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.APPLAUSE);
        return () => AudioService.release(AUDIO_EFFECT.APPLAUSE);
    }, []);

    useEffect(() => {
        if (!open) return;
        void AudioService.play(AUDIO_EFFECT.APPLAUSE);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const el = hostRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const measure = () => {
            const rect = el.getBoundingClientRect();
            setSize({
                w: Math.max(1, Math.round(rect.width)),
                h: Math.max(1, Math.round(rect.height)),
            });
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        position: "relative",
                        overflow: "hidden",
                        pt: 3.5,
                        px: 2.5,
                        pb: 2.5,
                        width: 300,
                        maxWidth: "calc(100vw - 32px)",
                        borderRadius: 3,
                        bgcolor: "#ecfdf5",
                        color: "#064e3b",
                        border: "1px solid rgba(16, 185, 129, 0.5)",
                        boxShadow: "0 18px 44px rgba(5, 150, 105, 0.28)",
                    },
                },
            }}
        >
            <Box ref={hostRef} sx={{ position: "relative", overflow: "hidden", pt: 1 }}>
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        zIndex: 0,
                    }}
                >
                    {open && size.w > 1 ? (
                        <Confetti
                            key={`ww-complete-${size.w}x${size.h}`}
                            width={size.w}
                            height={size.h}
                            recycle={false}
                            numberOfPieces={64}
                            gravity={0.22}
                            tweenDuration={4200}
                            confettiSource={{ x: 0, y: 0, w: size.w, h: 12 }}
                            style={{ pointerEvents: "none" }}
                        />
                    ) : null}
                </Box>
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                >
                    <Box
                        sx={{
                            width: 92,
                            height: 92,
                            mb: 1.5,
                            flexShrink: 0,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(52, 211, 153, 0.2)",
                            animation: "wwCompleteBounce 1.6s ease-in-out infinite",
                            "@keyframes wwCompleteBounce": {
                                "0%, 100%": { transform: "translateY(0) scale(1)" },
                                "50%": { transform: "translateY(-4px) scale(1.04)" },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 76,
                                height: 76,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#d1fae5",
                                boxShadow: "0 8px 20px rgba(5, 150, 105, 0.28)",
                            }}
                        >
                            <EmojiEventsIcon sx={{ fontSize: 46, color: "#059669" }} />
                        </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.3, mb: 2 }}>
                        {t("wordwheel.complete-title")}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                        <Button
                            onClick={onClose}
                            sx={{
                                flex: 1,
                                minHeight: 48,
                                borderRadius: 999,
                                fontWeight: 800,
                                color: "#047857",
                                border: "1.5px solid rgba(5, 150, 105, 0.45)",
                                bgcolor: "rgba(255, 255, 255, 0.7)",
                                "&:hover": { bgcolor: "#fff", borderColor: "#059669" },
                            }}
                        >
                            {t("wordwheel.close")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onContinue || onClose}
                            sx={{
                                flex: 1.3,
                                minHeight: 48,
                                borderRadius: 999,
                                fontWeight: 800,
                                fontSize: "0.95rem",
                                color: "#fff",
                                background: "linear-gradient(180deg, #34d399 0%, #059669 100%)",
                                boxShadow: "0 8px 18px rgba(5, 150, 105, 0.38)",
                                overflow: "hidden",
                                position: "relative",
                                animation: "wwCompleteGlow 1.8s ease-in-out infinite",
                                "@keyframes wwCompleteGlow": {
                                    "0%, 100%": { boxShadow: "0 8px 18px rgba(5, 150, 105, 0.38)" },
                                    "50%": { boxShadow: "0 10px 26px rgba(52, 211, 153, 0.7)" },
                                },
                                "&::after": {
                                    content: "\"\"",
                                    position: "absolute",
                                    inset: 0,
                                    background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)",
                                    transform: "translateX(-120%)",
                                    animation: "wwCompleteShine 2.2s ease-in-out infinite",
                                },
                                "@keyframes wwCompleteShine": {
                                    "0%": { transform: "translateX(-120%)" },
                                    "60%, 100%": { transform: "translateX(120%)" },
                                },
                                "&:hover": {
                                    background: "linear-gradient(180deg, #6ee7b7 0%, #047857 100%)",
                                },
                            }}
                        >
                            {isDaily ? t("wordwheel.done") : t("wordwheel.next-level")}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Dialog>
    );
}
